import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  applyConfiguration,
  maxConfigurationFileBytes,
  minimumPassphraseLength,
  previewConfiguration,
  type ConfigurationApplied,
  type ConfigurationPreview,
} from "../../api/configuration";
import { useToast } from "../../components/toast/toastContext";
import panel from "../../components/admin/AdminPanel.module.css";
import styles from "./ConfigurationContent.module.css";

type Busy = "preview" | "apply" | null;

/**
 * Previews, then applies, an uploaded configuration file. The apply always sends the fingerprint of the latest preview, and changing
 * the file or the passphrase clears that preview, so an apply can never run against a plan the administrator has not just reviewed.
 * The passphrase is cleared after an apply attempt and is never stored. The preview itself is owned by the page and shown beneath
 * both panels, so its table has the full width; this panel only reports changes to it.
 */
export function ConfigurationImportPanel({
  preview,
  onPreviewChange,
}: {
  preview: ConfigurationPreview | null;
  onPreviewChange: (preview: ConfigurationPreview | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const fileId = useId();
  const passphraseId = useId();
  const statusId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [applied, setApplied] = useState<ConfigurationApplied | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (applied !== null) {
      resultRef.current?.focus();
    }
  }, [applied]);

  const ready = file !== null && passphrase.length >= minimumPassphraseLength;

  const chooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    setApplied(null);
    onPreviewChange(null);
    if (chosen !== null && chosen.size > maxConfigurationFileBytes) {
      toast.show(t("configAdmin.import.tooLarge"), "error");
      setFile(null);
      event.target.value = "";
      return;
    }

    setFile(chosen);
  };

  const changePassphrase = (value: string) => {
    setPassphrase(value);
    onPreviewChange(null);
  };

  const runPreview = async () => {
    if (file === null || !ready) {
      return;
    }

    setBusy("preview");
    const result = await previewConfiguration(file, passphrase);
    setBusy(null);
    if (result.kind === "ok") {
      onPreviewChange(result.response);
    } else {
      onPreviewChange(null);
      toast.show(result.kind === "denied" ? t("configAdmin.denied.description") : result.message, "error");
    }
  };

  const runApply = async () => {
    if (file === null || preview?.canApply !== true) {
      return;
    }

    setBusy("apply");
    const result = await applyConfiguration(file, passphrase, preview.fingerprint);
    setBusy(null);
    setPassphrase("");
    onPreviewChange(null);
    if (result.kind === "ok") {
      setFile(null);
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = "";
      }
      setApplied(result.response);
      toast.show(t("configAdmin.import.applied", { created: result.response.created, updated: result.response.updated }), "success");
    } else if (result.kind === "conflict") {
      toast.show(t("configAdmin.import.planChanged"), "error");
    } else {
      toast.show(result.kind === "denied" ? t("configAdmin.denied.description") : result.message, "error");
    }
  };

  return (
    <section className={`${panel.panel} ${styles.section}`} aria-labelledby={`${fileId}-heading`}>
      <div className={panel.header}>
        <h2 id={`${fileId}-heading`} className={`panel-title ${panel.title}`}>
          {t("configAdmin.import.heading")}
        </h2>
      </div>
      <p className={panel.intro}>{t("configAdmin.import.description")}</p>
      <form
        className={styles.panel}
        aria-busy={busy !== null}
        onSubmit={(event) => {
          event.preventDefault();
          void runPreview();
        }}
      >
        <div className={styles.field}>
          <label htmlFor={fileId}>{t("configAdmin.import.file")}</label>
          <input ref={fileInputRef} id={fileId} className={styles.fileInput} type="file" accept=".aiic" onChange={chooseFile} aria-describedby={statusId} />
          <p id={statusId} className={file === null ? "visually-hidden" : styles.hint}>
            {file === null ? t("configAdmin.import.noFile") : t("configAdmin.import.chosen", { name: file.name })}
          </p>
        </div>
        <div className={styles.field}>
          <label htmlFor={passphraseId}>{t("configAdmin.import.passphrase")}</label>
          <input
            id={passphraseId}
            type="password"
            autoComplete="off"
            value={passphrase}
            aria-describedby={`${passphraseId}-hint`}
            onChange={(event) => changePassphrase(event.target.value)}
          />
          <p id={`${passphraseId}-hint`} className={styles.hint}>
            {t("configAdmin.import.passphraseHint")}
          </p>
        </div>
        <div className={styles.actions}>
          <button type="submit" className="button button-secondary" disabled={!ready || busy !== null}>
            {busy === "preview" && <span className={styles.spinner} aria-hidden="true" />}
            {t("configAdmin.import.preview")}
          </button>
          <button
            type="button"
            className="button button-primary"
            disabled={busy !== null || preview?.canApply !== true}
            onClick={() => void runApply()}
          >
            {busy === "apply" && <span className={styles.spinner} aria-hidden="true" />}
            {t("configAdmin.import.apply")}
          </button>
        </div>
      </form>

      <p className={styles.hint} role="status" aria-live="polite">
        {busy === "preview" && t("configAdmin.import.previewing")}
        {busy === "apply" && t("configAdmin.import.applying")}
        {busy === null && preview === null && applied === null && !ready && t("configAdmin.import.empty")}
      </p>

      {applied !== null && (
        <div ref={resultRef} tabIndex={-1} className={styles.summary} aria-labelledby={`${statusId}-result`}>
          <h3 id={`${statusId}-result`}>{t("configAdmin.import.applied", { created: applied.created, updated: applied.updated })}</h3>
          <p>
            {applied.instanceName} · {applied.fingerprint}
          </p>
        </div>
      )}
    </section>
  );
}
